// -1 no free space at all
//  0 has free space but it's not enough for the time shift  
//  1 has space to fill the time shift
const TimelineShiftInfo = {
    NO_SPACE: -1,
    NOT_ENOUGH: 0,
    HAS_SPACE: 1
}

export const convertToLyricsTime = (time) => {
    return parseFloat((time).toFixed(1));
};

export function getMiddlePointBounds(segments, middlePointTime, maxDuration) {
    if (segments && segments.length > 0) {
        // Check the bounds of first segment
        const firstSegment = segments[0];
        if (middlePointTime >= 0 && middlePointTime <= firstSegment.startTime)
            return { top: 0, bottom: firstSegment.startTime };

        // Check the bounds of all the middle segments
        for (let i = 0; i < segments.length - 1; i++) {
            const currentSegment = segments[i];
            const nextSegment = segments[i + 1];

            if (middlePointTime >= currentSegment.endTime && middlePointTime <= nextSegment.startTime)
                return { top: currentSegment.endTime, bottom: nextSegment.startTime };
        }

        // Check the bounds of last segment
        const lastSegment = segments[segments.length - 1];
        if (middlePointTime >= lastSegment.endTime && middlePointTime <= maxDuration)
            return { top: lastSegment.endTime, bottom: maxDuration };

        // Returns negative bounds if we have the segments but a middle point isn't between them
        return { top: -1, bottom: -1 };
    }

    // Returns the full range if there are not any segments
    return { top: 0, bottom: maxDuration };
}

export function getSegmentBounds(segments, compareSegmentIndex, maxDuration) {
    let bounds = { 
        top: 0, 
        bottom: maxDuration 
    };

    if (segments && segments.length > 0) {
        const previousSegment = segments[compareSegmentIndex - 1];
        const nextSegment = segments[compareSegmentIndex + 1];

        if (previousSegment)
            bounds.top = previousSegment.endTime;
        
        if (nextSegment)
            bounds.bottom = nextSegment.startTime;
    }

    return bounds;
}

export function findSpaceForSegment(segments, targetStartTime, targetDuration, maxDuration) {
    if (segments && segments.length > 0) {
        // When it's the top border and first segment
        const firstSegment = segments[0];

        if (targetStartTime <= firstSegment.startTime) {
            const availableDuration = firstSegment.startTime;

            if (availableDuration >= targetDuration)
                return { top: 0, bottom: firstSegment.startTime };
        }

        // When it's middle segment
        let sectionFoundWithNoSpace = false;

        for(let i = 0; i < segments.length - 1; i++) {
            const currentSegment = segments[i];
            const nextSegment = segments[i + 1];
            const availableDuration = nextSegment.startTime - currentSegment.endTime;

            if (!sectionFoundWithNoSpace) {
                if (targetStartTime >= currentSegment.startTime && targetStartTime <= nextSegment.startTime) {
                    // Notify that we've found a target section
                    sectionFoundWithNoSpace = true;

                    // Return the bounds if a section has enough space to fit the segment
                    if (availableDuration >= targetDuration)
                        return { top: currentSegment.endTime, bottom: nextSegment.startTime };
                }
            } else {
                // Find the next alternative place to fit the segment
                if (availableDuration >= targetDuration)
                    return { top: currentSegment.endTime, bottom: nextSegment.startTime };
            }
        }

        // When it's the last segment and bottom border
        const lastSegment = segments[segments.length - 1];

        if (targetStartTime >= lastSegment.startTime || sectionFoundWithNoSpace) {
            const availableDuration = maxDuration - lastSegment.endTime;

            if (availableDuration >= targetDuration)
                return { top: lastSegment.endTime, bottom: maxDuration };
        }

        // If there is not empty space
        return { top: undefined, bottom: undefined };
    }

    return { top: 0, bottom: maxDuration };
}

export function hasSegmentCollision(startTime, endTime, segments, audioDuration) {
    if (endTime > audioDuration)
        return true;

    if (segments && segments.length > 0) {
        for (let i = 0; i < segments.length; i++) {
            let segment = segments[i];

            if (startTime > segment.startTime && startTime < segment.endTime)
                return true;

            if (endTime > segment.startTime && endTime < segment.endTime)
                return true;
        }
    }

    return false;
}

const createPostponeResultObject = (isPostponed, postponedSegments) => {
    return {
        isPostponed,
        postponedSegments
    };
};

const createPostponedSegmentObject = (currentSegment, startTime, endTime) => {
    return {
        ...currentSegment,
        startTime,
        endTime
    };
};

const buildAccordionFromTop = (segments, lastSegmentIndex) => {
    let lastEndTime = 0;

    for (let i = 0; i <= lastSegmentIndex; i++) {
        const segment = segments[i];
        const duration = convertToLyricsTime(segment.endTime - segment.startTime); 

        const newStartTime = lastEndTime;
        const newEndTime = convertToLyricsTime(lastEndTime + duration);

        if (newStartTime !== segment.startTime || newEndTime !== segment.endTime)
            segments[i] = createPostponedSegmentObject(segment, newStartTime, newEndTime);

        lastEndTime = newEndTime;
    }
};

const buildAccordionFromBottom = (segments, lastSegmentIndex, audioDuration) => {
    let lastStartTime = audioDuration;

    for (let i = segments.length - 1; i >= lastSegmentIndex; i--) {
        const segment = segments[i];
        const duration = convertToLyricsTime(segment.endTime - segment.startTime); 

        const newStartTime = convertToLyricsTime(lastStartTime - duration);
        const newEndTime = lastStartTime;

        if (newStartTime !== segment.startTime || newEndTime !== segment.endTime)
            segments[i] = createPostponedSegmentObject(segment, newStartTime, newEndTime);

        lastStartTime = newStartTime;
    }
};

// NO_SPACE - no free space at all
// NOT_ENOUGH - has free space but it's not enough for the time shift  
// HAS_SPACE - has space to fill the time shift
const canPostponeTopSegment = (segments, index, timeShift) => {
    let allAvailableSpace = 0; 

    for (let i = index; i >= 0; i--) {
        const currentSegment = segments[i];
        const topSegment = segments[i - 1];

        const topBound = (topSegment ? topSegment.endTime : 0);
        const timeBetweenSegments = convertToLyricsTime(currentSegment.startTime - topBound); 
        allAvailableSpace = convertToLyricsTime(allAvailableSpace + timeBetweenSegments); 

        if (timeBetweenSegments >= timeShift) {
            return TimelineShiftInfo.HAS_SPACE;
        }
    }

    return allAvailableSpace > 0 ? TimelineShiftInfo.NOT_ENOUGH : TimelineShiftInfo.NO_SPACE;
};

// NO_SPACE - no free space at all
// NOT_ENOUGH - has free space but it's not enough for the time shift  
// HAS_SPACE - has space to fill the time shift
const canPostponeBottomSegment = (segments, index, timeShift, audioDuration) => {
    let allAvailableSpace = 0; 

    for (let i = index; i < segments.length; i++) {
        const currentSegment = segments[i];
        const bottomSegment = segments[i + 1];

        const bottomBound = (bottomSegment ? bottomSegment.startTime : audioDuration);
        const timeBetweenSegments = convertToLyricsTime(bottomBound - currentSegment.endTime); 
        allAvailableSpace = convertToLyricsTime(allAvailableSpace + timeBetweenSegments); 

        if (timeBetweenSegments >= timeShift) {
            return TimelineShiftInfo.HAS_SPACE;
        }
    }

    return allAvailableSpace > 0 ? TimelineShiftInfo.NOT_ENOUGH : TimelineShiftInfo.NO_SPACE;
};

export function tryPostponeTopSegments(segments, startIndex, startTime, endTime) {
    const result = createPostponeResultObject(false, undefined);

    if (segments && segments.length && startIndex >= 0) {
        let lastStartTimeBound = startTime;
        
        for (let i = startIndex; i >= 0; i--) {
            const currentSegment = segments[i];
            const topSegment = segments[i - 1];

            const timeShift = currentSegment.endTime - lastStartTimeBound;
            const topBound = topSegment ? topSegment.endTime : 0;

            const postponedStartTime = convertToLyricsTime(currentSegment.startTime - timeShift);
            const postponedEndTime = convertToLyricsTime(currentSegment.endTime - timeShift);

            // When there is enough space, we just postpone current segment and stop moving
            // Otherwise, if there is a collision with a top one, try to postpone top segment in order to release space for current one
            if (postponedStartTime >= topBound) {
                segments[i] = createPostponedSegmentObject(currentSegment, postponedStartTime, postponedEndTime);
                result.isPostponed = true;
                break;
            } else {
                const topSegmentTimeShift = convertToLyricsTime(topBound - postponedStartTime);
                const checkResult = canPostponeTopSegment(segments, i - 1, topSegmentTimeShift);

                if (checkResult === TimelineShiftInfo.HAS_SPACE) {
                    // Postpone current segment if it's possible to postpone a top one, and continue moving in order to check a next segment

                    segments[i] = createPostponedSegmentObject(currentSegment, postponedStartTime, postponedEndTime);
                    lastStartTimeBound = postponedStartTime;
                    result.isPostponed = true;
                } else if (checkResult === TimelineShiftInfo.NOT_ENOUGH) {
                    // Built the accordion from top to bottom if there is no space to fill cursor shift and we steel have space
                    
                    buildAccordionFromTop(segments, i);
                    result.isPostponed = true;
                    break;
                } else {
                    // When the previous segments returned NO_SPACE but it can be some space after first segment
                    if ((currentSegment.startTime - topBound) > 0) {
                        buildAccordionFromTop(segments, i);
                        result.isPostponed = true;
                    }

                    // We stop moving because there is not space at the timeline at all
                    break;
                }
            }
        }
    }

    if (result.isPostponed)
        result.postponedSegments = segments;

    return result;
}

export function tryPostponeBottomSegments(segments, startIndex, startTime, endTime, audioDuration) {
    const result = createPostponeResultObject(false, undefined);

    if (segments && segments.length && startIndex < segments.length) {
        let lastEndTimeBound = endTime;

        for (let i = startIndex; i < segments.length; i++) {
            const currentSegment = segments[i];
            const bottomSegment = segments[i + 1];

            const timeShift = lastEndTimeBound - currentSegment.startTime;
            const bottomBound = bottomSegment ? bottomSegment.startTime : audioDuration;

            const postponedStartTime = convertToLyricsTime(currentSegment.startTime + timeShift);
            const postponedEndTime = convertToLyricsTime(currentSegment.endTime + timeShift);

            // When there is enough space, we just postpone current segment and stop moving
            // Otherwise, if there is a collision with a bottom one, try to postpone bottom segment in order to release space for current one
            if (postponedEndTime <= bottomBound) {
                segments[i] = createPostponedSegmentObject(currentSegment, postponedStartTime, postponedEndTime);
                result.isPostponed = true;
                break;
            } else {
                const bottomSegmentTimeShift = convertToLyricsTime(postponedEndTime - bottomBound);
                const checkResult = canPostponeBottomSegment(segments, i + 1, bottomSegmentTimeShift, audioDuration);

                if (checkResult === TimelineShiftInfo.HAS_SPACE) {
                    // Postpone current segment if it's possible to postpone a bottom one and continue moving

                    segments[i] = createPostponedSegmentObject(currentSegment, postponedStartTime, postponedEndTime);
                    lastEndTimeBound = postponedEndTime;
                    result.isPostponed = true;
                } else if (checkResult === TimelineShiftInfo.NOT_ENOUGH) {
                    // Built the accordion from top to bottom if there is no space to fill cursor shift and we steel have space

                    buildAccordionFromBottom(segments, i, audioDuration);
                    result.isPostponed = true;
                    break;
                } else {
                    // When the next segments returned NO_SPACE but it can be some space after first segment
                    if ((bottomBound - currentSegment.endTime) > 0) {
                        buildAccordionFromBottom(segments, i, audioDuration);
                        result.isPostponed = true;
                    }

                    // We stop moving because there is not space at the timeline at all
                    break;
                }
            }
        }
    }

    if (result.isPostponed)
        result.postponedSegments = segments;

    return result;
}